import { create } from 'zustand';
import { listRoomPolls, votePoll, closePoll, type Poll } from '../api/chat-polls';

/**
 * 대화방 투표 상태 (chat-polls).
 *
 * 카드는 메시지 목록 안에서 그려지고(말풍선 자리), 만들기는 정보 패널에서 한다 — 둘이 같은
 * 투표를 봐야 하므로 상태를 밖에 둔다.
 *
 * 서버가 아직 이 창구를 모르면(404) 조용히 빈 채로 둔다 — 투표 카드만 안 뜨고 대화는 그대로다.
 */
interface ChatPollState {
  /** roomId → 그 방의 투표들. */
  pollsByRoom: Record<string, Poll[]>;
  loadingRooms: Record<string, boolean>;
  load: (roomId: string, opts?: { force?: boolean }) => Promise<void>;
  upsert: (poll: Poll) => void;
  vote: (pollId: string, optionIds: string[]) => Promise<void>;
  close: (pollId: string) => Promise<void>;
  reset: () => void;
}

export const useChatPollStore = create<ChatPollState>((set, get) => ({
  pollsByRoom: {},
  loadingRooms: {},

  load: async (roomId, opts) => {
    if (!opts?.force && get().pollsByRoom[roomId]) return;
    if (get().loadingRooms[roomId]) return;
    set((s) => ({ loadingRooms: { ...s.loadingRooms, [roomId]: true } }));
    try {
      const polls = await listRoomPolls(roomId);
      set((s) => ({ pollsByRoom: { ...s.pollsByRoom, [roomId]: polls } }));
    } catch {
      // 서버가 아직 모르거나 잠깐 실패 — 카드만 안 뜬다.
      set((s) => ({ pollsByRoom: { ...s.pollsByRoom, [roomId]: s.pollsByRoom[roomId] ?? [] } }));
    } finally {
      set((s) => ({ loadingRooms: { ...s.loadingRooms, [roomId]: false } }));
    }
  },

  upsert: (poll) => set((s) => {
    const list = s.pollsByRoom[poll.roomId] ?? [];
    const i = list.findIndex((p) => p.id === poll.id);
    const next = i >= 0 ? list.map((p) => (p.id === poll.id ? poll : p)) : [poll, ...list];
    return { pollsByRoom: { ...s.pollsByRoom, [poll.roomId]: next } };
  }),

  vote: async (pollId, optionIds) => {
    const updated = await votePoll(pollId, optionIds);
    get().upsert(updated);
  },

  close: async (pollId) => {
    const updated = await closePoll(pollId);
    get().upsert(updated);
  },

  reset: () => set({ pollsByRoom: {}, loadingRooms: {} }),
}));

/** 이 메시지 자리에 그릴 투표(없으면 null). */
export function pollForMessage(
  polls: Poll[] | undefined, messageId: string | null | undefined,
): Poll | null {
  if (!polls || !messageId) return null;
  return polls.find((p) => p.messageId === messageId) ?? null;
}
